import { useEffect } from 'react';
import { useQuery, QueryResult } from '@apollo/client';
// Api
import apolloNotificationClient from 'api/apolloNotificationClient';
import { GET_UNREAD_NOTIFICATIONS_COUNT } from 'api/notifications/queries';
import { ON_NOTIFICATION_CREATED } from 'api/notifications/subscriptions';
// Types
import { GetUnreadNotificationsCount } from 'api/notifications/types/GetUnreadNotificationsCount';
import {
  OnNotificationCreated,
  OnNotificationCreatedVariables,
} from 'api/notifications/types/OnNotificationCreated';
// Hooks
import { useGetCurrUser } from 'hooks/useGetCurrUser';
import { useHasMounted } from 'hooks/useHasMounted';

/**
 * Module-level variables to track last notification received
 * useRef is not used because it doesn't update on time, they are handled almost at the same time
 * this is because DesktopHeader and MobileHeader are rendered at the same time and both of them use this hook
 */
let lastProcessedNotificationId: string | null = null;
let lastProcessedTimestamp = 0;

/**
 * Hook to fetch the count of unread notifications with subscription support
 *
 * @returns The query result with unread notifications count
 */
export const useGetUnreadNotificationsCount = (): QueryResult<GetUnreadNotificationsCount> => {
  const hasMounted = useHasMounted();
  const { data: userData } = useGetCurrUser();
  const userId = userData?.me.id;
  const skip = !userId || !hasMounted;

  const result = useQuery<GetUnreadNotificationsCount>(
    GET_UNREAD_NOTIFICATIONS_COUNT,
    {
      client: apolloNotificationClient,
      skip,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  // Set up subscription to update count when notifications arrive
  useEffect(() => {
    if (skip || !result.data) return;

    // Subscribe to notification creations
    const unsubscribe = result.subscribeToMore<
      OnNotificationCreated,
      OnNotificationCreatedVariables
    >({
      document: ON_NOTIFICATION_CREATED,
      variables: { userId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        // Extract the new notification
        const newNotification = subscriptionData.data.onNotificationCreated;

        // Only update count for unread notifications
        if (!newNotification || newNotification.isRead) {
          return prev;
        }

        // Deduplicate notifications that arrive within a short time window
        const now = Date.now();
        if (
          lastProcessedNotificationId === newNotification.id &&
          now - lastProcessedTimestamp < 1000 // 1 second window
        ) {
          return prev;
        }

        // Update tracking variables
        lastProcessedNotificationId = newNotification.id;
        lastProcessedTimestamp = now;

        // Increment the count
        return {
          getUnreadNotificationsCount:
            (prev.getUnreadNotificationsCount || 0) + 1,
        };
      },
    });

    return () => unsubscribe();
  }, [userId, result, skip]);

  return result;
};
